import dayjs from 'dayjs';
import { Menu, Bell, User, Crown } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { handleMarkAllNotificationsRead, handleMarkNotificationRead } from '../../shared/controllers/notificationController';
import { formatDateTime } from '../../shared/utils/dateTime';
import { formatPriceValue } from '../../utils/price';
import { useSidebar } from '../app/providers';
import { NotificationItem, useNotificationsContext } from '../contexts/notificationContext';
import { useUserContext } from '../contexts/userContext';


const resolveNotificationContent = (item: NotificationItem) => {
    const payload = item.data || {};
    const title =
        (typeof payload.title === 'string' && payload.title.trim()) ||
        'Notification';
    const baseDescription =
        (typeof payload.description === 'string' && payload.description.trim()) ||
        (typeof payload.message === 'string' && payload.message.trim()) ||
        'You have a new update.';
    const type = typeof payload.type === 'string' ? payload.type.trim().toLowerCase() : '';
    const kickoff = typeof payload.kickoff === 'string' ? payload.kickoff.trim() : '';

    const hasKickoffInText = kickoff !== '' && baseDescription.toLowerCase().includes(kickoff.toLowerCase());
    const shouldAppendKickoff = kickoff !== '' && type.startsWith('game.') && !hasKickoffInText;

    const description = shouldAppendKickoff
        ? `${baseDescription} Scheduled: ${kickoff}.`
        : baseDescription;

    return { title, description };
};

const Header: React.FC = () => {
    const { userProfile } = useUserContext();
    const { isCollapsed, setIsMobileOpen } = useSidebar();
    const {
        notifications,
        unreadCount,
        loadingNotifications,
        refreshNotifications,
    } = useNotificationsContext();
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const isNewUser = useMemo(() => {
        if (!userProfile?.created_at) return false;
        return dayjs(userProfile.created_at).isAfter(dayjs().subtract(24, 'hour'));
    }, [userProfile?.created_at]);

    const isUserPro = userProfile?.is_pro === true || userProfile?.is_pro === 1;
    const navbarBalance = useMemo(() => {
        const amount = Number(
            userProfile?.balance ??
            userProfile?.wallet?.balance ??
            0
        );
        const currencyCode =
            userProfile?.active_currency ||
            userProfile?.wallet?.currency ||
            userProfile?.currency ||
            'NGN';

        return formatPriceValue(Number.isFinite(amount) ? amount : 0, { currencyCode });
    }, [userProfile]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (!panelRef.current) return;
            const target = event.target;
            if (target instanceof Node && !panelRef.current.contains(target)) {
                setIsNotificationsOpen(false);
            }
        };

        if (isNotificationsOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }

        return undefined;
    }, [isNotificationsOpen]);

    useEffect(() => {
        if (isNotificationsOpen) {
            refreshNotifications();
        }
    }, [isNotificationsOpen, refreshNotifications]);

    const onBellClick = () => {
        setIsNotificationsOpen((prev) => !prev);
    };

    const markAsRead = async (id: string | number, isUnread: boolean) => {
        if (!isUnread) return;
        try {
            await handleMarkNotificationRead('user', String(id));
            refreshNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await handleMarkAllNotificationsRead('user');
            refreshNotifications();
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    };

    return (
        <header
            className={`
                fixed top-0 right-0 z-30 h-16 bg-white border-b border-slate-200 transition-all duration-300
                ${isCollapsed ? 'left-0 lg:left-[72px]' : 'left-0 lg:left-64'}
            `}
        >
            <div className="h-full px-4 lg:px-6 flex items-center justify-between">
                {/* Left: Sidebar Toggle */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setIsMobileOpen(true)}
                        className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                    >
                        <Menu size={20} />
                    </button>

                    <h1 className="text-lg font-semibold text-slate-800 hidden sm:block">
                        {isNewUser ? 'Welcome' : 'Welcome back'}, {userProfile?.firstname || 'Player'}!
                    </h1>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center max-w-[120px] sm:max-w-none px-2 sm:px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50">
                        <span className="hidden sm:inline text-[11px] font-medium text-slate-500 mr-1.5">Balance</span>
                        <span className="text-xs sm:text-sm font-semibold text-slate-900 truncate">{navbarBalance}</span>
                    </div>

                    <div className="relative" ref={panelRef}>
                        <button
                            onClick={onBellClick}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full relative"
                            aria-label="Notifications"
                        >
                            <Bell size={20} />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center border border-white">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>

                        {isNotificationsOpen && (
                            <div className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl z-40">
                                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-900">Notifications</p>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={markAllAsRead}
                                            className="text-xs font-medium text-primary hover:text-primary-dark"
                                        >
                                            Mark all read
                                        </button>
                                    )}
                                </div>

                                {loadingNotifications && notifications.length === 0 ? (
                                    <div className="px-4 py-6 text-sm text-slate-500">Loading...</div>
                                ) : notifications.length === 0 ? (
                                    <div className="px-4 py-6 text-sm text-slate-500">No notifications yet.</div>
                                ) : (
                                    <div className="max-h-80 overflow-y-auto">
                                        {notifications.slice(0, 8).map((item) => {
                                            const { title, description } = resolveNotificationContent(item);
                                            const isUnread = item.read_at === null;

                                            return (
                                                <button
                                                    key={String(item.id)}
                                                    onClick={() => markAsRead(item.id, isUnread)}
                                                    className={`w-full px-4 py-3 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${isUnread ? 'bg-emerald-50/30' : 'bg-white'
                                                        }`}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        {isUnread && <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500" />}
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-slate-900 truncate">{title}</p>
                                                            <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{description}</p>
                                                            <p className="text-[11px] text-slate-400 mt-1">
                                                                {formatDateTime(item.created_at, undefined, '')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="h-8 w-[1px] bg-slate-200 mx-1"></div>

                    <div className="flex items-center gap-3 pl-2">
                        <div className="text-right hidden md:block">
                            <p className="text-sm font-medium text-slate-900">
                                {userProfile?.firstname} {userProfile?.lastname}
                            </p>
                            <div className="flex items-center justify-end gap-1.5">
                                {isUserPro && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-[10px] font-bold rounded-full">
                                        <Crown size={10} />
                                        PRO
                                    </span>
                                )}
                                <p className="text-xs text-slate-500">
                                    {userProfile?.role || 'Member'}
                                </p>
                            </div>
                        </div>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-medium ${isUserPro ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white ring-2 ring-yellow-300' : 'bg-primary/10 text-primary'}`}>
                            {userProfile?.firstname?.[0] || <User size={18} />}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
